import type { FilterAnchor } from '@ofmos/shared/filter-ast';
import type {
  CreateFieldInput,
  CreateObjectInput,
  DestructiveApplyOptions,
  DropTarget,
  FieldMetadata,
  InboundRelation,
  MetadataClient,
  MetadataVisibility,
  MigrationPreview,
  ObjectMetadata,
  UpdateFieldInput,
  UpdateObjectInput,
} from '../client';
import { toFieldList, toFieldMetadata, toObjectMetadata, toWireOptions } from './mapping';
import type { Transport } from './transport';
import { ApiError } from './transport';
import type {
  WireApplyResult,
  WireField,
  WireInboundRelations,
  WireMetadataGraph,
  WireObject,
} from './wire';

/**
 * `MetadataClient` over `/api/metadata/*`.
 *
 * Three things shape this file:
 *
 * ONE REQUEST FOR THE GRAPH. `GET /api/metadata` returns objects WITH their fields, and
 * the UI needs the fields to derive `labelIdentifierFieldId` for any object it renders.
 * Fetching per object would mean N+1 requests to answer "what is this record called".
 * The graph is held until it is invalidated, which happens on any mutation through this
 * client and on `refresh()` - which the live stream calls when the metadata version moves
 * (that is the only other way the model can change).
 *
 * MUTATIONS RETURN A PLAN, NOT A ROW. The engine answers with `ApplyResult` (a two-phase
 * migration plan), so a create reads its new row out of the plan's `CREATE_OBJECT` /
 * `CREATE_FIELD` action - which carries the full spec - and an update re-reads the graph.
 * That is the honest mapping: there is no "created row" in the response to return.
 *
 * DESTRUCTIVE = DRY RUN FIRST (admin-area.md D5). The plan token is fetched deliberately
 * from `POST /api/metadata/dry-run` BEFORE the dialog opens, and the dialog sends it back
 * with the `affectedRows` figures it displayed (D6). Scraping the token off a 409 the
 * client provoked by calling the destructive endpoint hopefully still works and is still
 * here - as the BACKSTOP for a plan that drifted between preview and confirm - but it is
 * no longer the primary path, because a client that has to provoke a refusal to learn the
 * plan has, by construction, never shown the plan to anybody.
 */
export class HttpMetadataClient implements MetadataClient {
  private graph: Promise<WireMetadataGraph> | null = null;

  constructor(private readonly transport: Transport) {}

  refresh(): void {
    this.graph = null;
  }

  private load(): Promise<WireMetadataGraph> {
    this.graph ??= this.transport
      .request<WireMetadataGraph>({ method: 'GET', path: '/api/metadata' })
      .catch((error: unknown) => {
        // A failed load must not be cached, or the app never recovers from one blip.
        this.graph = null;
        throw error;
      });
    return this.graph;
  }

  /**
   * The objects a query at this visibility may see (admin-area.md D8, D10).
   *
   * `visible` is the line that has kept archived objects and INV-9 junction objects out
   * of navigation, out of every record screen and out of every relation picker since
   * WP1. It is unchanged and it is the DEFAULT: only the two admin screens pass `all`,
   * and if that ever became the default every junction table would appear in everybody's
   * sidebar - the exact failure D10 names.
   */
  private async scopedObjects(
    visibility: MetadataVisibility,
  ): Promise<readonly (WireObject & { readonly fields: readonly WireField[] })[]> {
    const graph = await this.load();
    if (visibility === 'all') return graph.objects;
    return graph.objects.filter((object) => object.deletedAt === null && !object.isHidden);
  }

  private async requireObject(objectId: string, visibility: MetadataVisibility = 'visible') {
    const object = (await this.scopedObjects(visibility)).find(
      (candidate) => candidate.id === objectId,
    );
    if (object === undefined) {
      throw new ApiError(`Object ${objectId} does not exist.`, {
        code: 'not_found',
        status: 404,
      });
    }
    return object;
  }

  /** Field rows including the system ones: the record client needs their ids. */
  async wireFieldsOf(objectId: string): Promise<readonly WireField[]> {
    return (await this.requireObject(objectId)).fields;
  }

  async listObjects(
    visibility: MetadataVisibility = 'visible',
  ): Promise<readonly ObjectMetadata[]> {
    return (await this.scopedObjects(visibility)).map((object) =>
      toObjectMetadata(object, object.fields),
    );
  }

  async getObject(
    objectId: string,
    visibility: MetadataVisibility = 'visible',
  ): Promise<ObjectMetadata> {
    const object = await this.requireObject(objectId, visibility);
    return toObjectMetadata(object, object.fields);
  }

  async listFields(
    objectId: string,
    visibility: MetadataVisibility = 'visible',
  ): Promise<readonly FieldMetadata[]> {
    return toFieldList((await this.requireObject(objectId, visibility)).fields, visibility);
  }

  /**
   * D2's read, mapped and NOT re-filtered.
   *
   * The route already dropped archived fields, archived objects and hidden ones, and
   * already ordered the list `(nav-group rank, child position, field position)`. Every
   * one of those is a decision this client must not repeat: re-sorting here would fork
   * the ordering rule, and re-filtering would need the graph that this read exists to
   * avoid walking.
   *
   * `toFieldMetadata` rather than `toFieldList`, because `isDisplayableField` drops
   * SYSTEM fields - and a junction's two endpoints are system fields. Dropping them
   * would delete exactly the N:N sections D3 builds.
   */
  async inboundRelations(objectId: string): Promise<readonly InboundRelation[]> {
    const response = await this.transport.request<WireInboundRelations>({
      method: 'GET',
      path: `/api/metadata/objects/${objectId}/inbound-relations`,
    });
    const graph = await this.load();
    return response.relations.map((entry) => ({
      field: toFieldMetadata(entry.field, entry.field.position),
      /**
       * The object's fields come from the GRAPH, not from this response, because
       * `toObjectMetadata` derives `labelIdentifierFieldId` from them - and a section
       * that could not name its own rows would render a column of "Untitled".
       */
      object: toObjectMetadata(
        entry.object,
        graph.objects.find((candidate) => candidate.id === entry.object.id)?.fields ?? [],
      ),
    }));
  }

  /**
   * D27's read, straight through: the payload IS the contract, so nothing is mapped.
   *
   * Every other read in this file rebuilds a wire row into the app's own metadata shape,
   * because those rows are field and object rows and the app's shapes are richer. An
   * anchor is not a row - it is the server's ANSWER to "what may anchor a related filter
   * here", already carrying its label and its derived direction - so mapping it would be
   * a second place that decides what an anchor is.
   */
  async filterAnchors(objectId: string): Promise<readonly FilterAnchor[]> {
    const response = await this.transport.request<{ readonly anchors: readonly FilterAnchor[] }>({
      method: 'GET',
      path: `/api/metadata/objects/${objectId}/filter-anchors`,
    });
    return response.anchors;
  }

  private async apply<T>(
    call: () => Promise<WireApplyResult>,
    read: (result: WireApplyResult) => T,
  ): Promise<T> {
    const result = await call();
    this.refresh();
    return read(result);
  }

  async createObject(input: CreateObjectInput): Promise<ObjectMetadata> {
    return this.apply(
      () =>
        this.transport.request<WireApplyResult>({
          method: 'POST',
          path: '/api/metadata/objects',
          body: {
            name: input.name,
            label: input.labelSingular,
            labelPlural: input.labelPlural,
            ...(input.description === undefined ? {} : { description: input.description }),
            /**
             * THE CREATE DIALOG HAS THE SAME TWO PICKERS AND HAD THE SAME BUG.
             * `NewObjectDialog` collects an icon and hands it over; this body dropped it,
             * so an object made with the calendar glyph opened with the derived one. The
             * route has accepted `icon` since D13 and `color` since E9.
             */
            ...(input.icon === undefined ? {} : { icon: input.icon }),
            ...(input.color === undefined ? {} : { color: input.color }),
          },
        }),
      (result) => {
        const object = createdObject(result);
        if (object === undefined) throw missingFromPlan('object');
        /**
         * Its fields are not in this plan (the engine creates the system columns as part
         * of the table), so the label identifier is resolved on the next graph read. The
         * hook invalidates `fields(objectId)` right after this, which is that read.
         */
        return toObjectMetadata(object, []);
      },
    );
  }

  async updateObject(objectId: string, input: UpdateObjectInput): Promise<ObjectMetadata> {
    await this.transport.request<WireApplyResult>({
      method: 'PATCH',
      path: `/api/metadata/objects/${objectId}`,
      body: {
        ...(input.name === undefined ? {} : { name: input.name }),
        ...(input.labelSingular === undefined ? {} : { label: input.labelSingular }),
        ...(input.labelPlural === undefined ? {} : { labelPlural: input.labelPlural }),
        ...(input.description === undefined ? {} : { description: input.description }),
        ...(input.position === undefined ? {} : { position: input.position }),
        ...(input.isHidden === undefined ? {} : { isHidden: input.isHidden }),
        /**
         * THE TWO PRESENTATION ATTRIBUTES, AND `icon` WAS MISSING FOR WEEKS (F3-10,
         * fixed 2026-09-08).
         *
         * The comment that used to stand below this body said `icon` "is derived from the
         * api name and the server stores no such column". That was true until
         * `record-page-and-views.md` D13; migration `0010_views` added
         * `meta.object_metadata.icon`, `metadata-operations.ts` accepts it on create and
         * update, and the seed writes it. The comment was never re-read, so the object
         * settings form's icon picker was the write-only theatre D12 removed from the
         * title field one control to the left: the browser reported success for a change
         * that never left it.
         *
         * `null` IS A VALUE for both of them - "no colour", "derive the icon" - so the
         * guard is `undefined` alone. A body that spread a null away would make CLEARING
         * either one impossible, which is the same bug in the other direction.
         */
        ...(input.icon === undefined ? {} : { icon: input.icon }),
        ...(input.color === undefined ? {} : { color: input.color }),
      },
    });
    this.refresh();
    /**
     * `all`, not `visible`: an admin who just hid an object still has to get it back.
     */
    return this.getObject(objectId, 'all');
  }

  async deleteObject(
    objectId: string,
    confirmToken: string,
    options: DestructiveApplyOptions = {},
  ): Promise<void> {
    requireHumanConfirmation(confirmToken);
    await this.destructive(
      (token) => ({
        method: 'DELETE',
        path: `/api/metadata/objects/${objectId}`,
        body: destructiveBody(token, options),
      }),
      options,
    );
    this.refresh();
  }

  /**
   * ARCHIVE AND RESTORE (D9), over the four routes WP11a added.
   *
   * Archive is the SAFE DEFAULT action in both lists and drop lives behind it, so a
   * destructive plan preview is never the first thing a user meets. Neither call is
   * destructive, so neither needs a token of any kind - which is the whole point of
   * making the reversible action the easy one.
   */
  async archiveObject(objectId: string): Promise<void> {
    await this.transport.request<WireApplyResult>({
      method: 'POST',
      path: `/api/metadata/objects/${objectId}/archive`,
      body: {},
    });
    this.refresh();
  }

  async restoreObject(objectId: string): Promise<void> {
    await this.transport.request<WireApplyResult>({
      method: 'POST',
      path: `/api/metadata/objects/${objectId}/restore`,
      body: {},
    });
    this.refresh();
  }

  async archiveField(fieldId: string): Promise<void> {
    await this.transport.request<WireApplyResult>({
      method: 'POST',
      path: `/api/metadata/fields/${fieldId}/archive`,
      body: {},
    });
    this.refresh();
  }

  async restoreField(fieldId: string): Promise<void> {
    await this.transport.request<WireApplyResult>({
      method: 'POST',
      path: `/api/metadata/fields/${fieldId}/restore`,
      body: {},
    });
    this.refresh();
  }

  /**
   * D5, the deliberate fetch. `POST /api/metadata/dry-run` renders the plan and stops:
   * no confirm token is demanded, nothing is applied, and the answer carries both the
   * token for THIS plan and the preflight rows with their `affectedRows` and their
   * webhook warnings.
   */
  async previewDrop(target: DropTarget): Promise<MigrationPreview> {
    const result = await this.transport.request<WireApplyResult>({
      method: 'POST',
      path: '/api/metadata/dry-run',
      body:
        target.kind === 'object'
          ? { operation: 'dropObject', objectId: target.objectId }
          : { operation: 'dropField', fieldId: target.fieldId },
    });
    return {
      phaseA: result.plan.phaseA,
      phaseB: result.plan.phaseB,
      destructive: result.plan.destructive,
      confirmToken: result.plan.confirmToken,
      preflight: result.preflight ?? [],
    };
  }

  /**
   * D12. TWO PATCHES, and the ORDER is the decision: the previous holder is cleared
   * FIRST, so the state between the two calls has zero flagged fields (the rule falls
   * back to a field named `name`, then to the first TEXT field) instead of two, which
   * the rule resolves arbitrarily. There is no batch operation on the metadata API -
   * `metadataOperationSchema` is a discriminated union of ONE operation per call - so
   * "in the same update" is not something this client can ask for.
   *
   * The whole `settings` object is sent because the server REPLACES it
   * (`withFieldPatch` spreads the patch over the spec). Sending only `isLabel` would
   * delete the identity declaration and the uniqueness flag of the same field.
   */
  async setLabelField(objectId: string, fieldId: string | null): Promise<void> {
    const fields = (await this.requireObject(objectId, 'all')).fields;
    const patch = async (field: WireField, isLabel: boolean): Promise<void> => {
      const { isLabel: _cleared, ...rest } = field.settings;
      await this.transport.request<WireApplyResult>({
        method: 'PATCH',
        path: `/api/metadata/fields/${field.id}`,
        body: { settings: isLabel ? { ...rest, isLabel: true } : rest },
      });
    };

    for (const field of fields) {
      if (field.id !== fieldId && field.settings.isLabel === true) await patch(field, false);
    }
    const target = fields.find((field) => field.id === fieldId);
    if (target !== undefined && target.settings.isLabel !== true) await patch(target, true);
    this.refresh();
  }

  async createField(input: CreateFieldInput): Promise<FieldMetadata> {
    const options = toWireOptions(input.options?.selectOptions);
    return this.apply(
      () =>
        this.transport.request<WireApplyResult>({
          method: 'POST',
          path: `/api/metadata/objects/${input.objectId}/fields`,
          body: {
            name: input.name,
            label: input.label,
            type: input.type,
            ...(input.description === undefined ? {} : { description: input.description }),
            ...(options === undefined ? {} : { options }),
            ...settingsOf(input),
          },
        }),
      (result) => {
        const field = createdField(result);
        if (field === undefined) throw missingFromPlan('field');
        // The engine appends and assigns the position; a CREATE cannot set one (D7).
        return toFieldMetadata(field, field.position);
      },
    );
  }

  async updateField(fieldId: string, input: UpdateFieldInput): Promise<FieldMetadata> {
    const options = toWireOptions(input.options?.selectOptions);
    await this.transport.request<WireApplyResult>({
      method: 'PATCH',
      path: `/api/metadata/fields/${fieldId}`,
      body: {
        ...(input.name === undefined ? {} : { name: input.name }),
        ...(input.label === undefined ? {} : { label: input.label }),
        ...(input.description === undefined ? {} : { description: input.description }),
        ...(input.position === undefined ? {} : { position: input.position }),
        ...(input.isHidden === undefined ? {} : { isHidden: input.isHidden }),
        ...settingsOf(input),
      },
    });
    /**
     * Options are their own route: `setSelectOptions` is where "same id = same option,
     * new label = new option, missing id = removal" lives, and removing an option is
     * DESTRUCTIVE (it drops an enum label rows may hold), so it needs the confirm flow.
     */
    if (options !== undefined && options.length > 0) {
      await this.destructive((token) => ({
        method: 'PUT',
        path: `/api/metadata/fields/${fieldId}/options`,
        body: { options, ...(token === undefined ? {} : { confirmToken: token }) },
      }));
    }
    this.refresh();
    return this.requireField(fieldId);
  }

  async deleteField(
    fieldId: string,
    confirmToken: string,
    options: DestructiveApplyOptions = {},
  ): Promise<void> {
    requireHumanConfirmation(confirmToken);
    await this.destructive(
      (token) => ({
        method: 'DELETE',
        path: `/api/metadata/fields/${fieldId}`,
        body: destructiveBody(token, options),
      }),
      options,
    );
    this.refresh();
  }

  private async requireField(fieldId: string): Promise<FieldMetadata> {
    const graph = await this.load();
    for (const object of graph.objects) {
      // `all`: an admin who just hid a field still gets the saved row back.
      const found = toFieldList(object.fields, 'all').find((field) => field.id === fieldId);
      if (found !== undefined) return found;
    }
    throw new ApiError(`Field ${fieldId} does not exist.`, { code: 'not_found', status: 404 });
  }

  /**
   * The INV-8 destructive flow: the plan token from the preview, with the 409 as backstop.
   *
   * PRIMARY PATH (D5): the dialog already ran the dry run, so `options.planToken` is the
   * token for the plan the user was SHOWN and the first call carries it.
   *
   * BACKSTOP: without a token - a caller that never previewed - the first call goes out
   * empty, the server refuses with `confirmation_required` and hands back the token for
   * THAT plan, and the second call sends it. Same for a plan that DRIFTED between preview
   * and confirm: the stale token is refused, and the retry is what re-reads the current
   * one.
   *
   * `preflight_drift` is never retried. It means the destruction GREW since the preview,
   * so repeating the call with a fresh token would destroy exactly what the refusal
   * exists to stop. It travels up with its numbers so the dialog can say what changed.
   *
   * Neither is `confirmation_mismatch`, WHEN A PREVIEW PRODUCED THE TOKEN - see below.
   */
  private async destructive(
    build: (confirmToken: string | undefined) => {
      method: 'DELETE' | 'PUT';
      path: string;
      body: unknown;
    },
    options: DestructiveApplyOptions = {},
  ): Promise<void> {
    try {
      await this.transport.request<WireApplyResult>(build(options.planToken));
      return;
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (error.code !== 'confirmation_required' && error.code !== 'confirmation_mismatch') {
        throw error;
      }
      /**
       * A PREVIEWED CALL IS NEVER SILENTLY RE-CONFIRMED.
       *
       * `confirmation_mismatch` means the plan CHANGED between the preview and the apply.
       * Taking the new plan's token off the refusal and sending it would apply a plan the
       * user was never shown - and the dialog's whole claim is that the token proves
       * which plan they saw. D6's hole 2 walks straight through that door: a
       * `SOFT_DELETE_OBJECT` for a MANY_TO_MANY junction is not destructive, so it is in
       * neither the token nor the preflight, and a junction restored in the meantime
       * would be archived by a plan whose preview never mentioned it.
       *
       * So the retry survives ONLY for the tokenless path - a script driving a drop with
       * no preview, which is what D5 keeps the 409 backstop for. When a dialog previewed,
       * the mismatch travels up and the dialog re-reads the plan and asks again.
       */
      if (options.planToken !== undefined) throw error;
      const token = error.confirmToken;
      if (token === undefined) throw error;
      await this.transport.request<WireApplyResult>(build(token));
    }
  }
}

/**
 * The body of a destructive call: the plan token, and the counts the dialog DISPLAYED.
 *
 * Both keys are omitted when absent rather than sent as `undefined`: the server's
 * `applyRequestSchema` is happy either way, but an omitted `expectedAffectedRows` is what
 * tells `guardDestructive` "this caller never rendered a preview", which is a legitimate
 * state for a script and not a refusal.
 */
function destructiveBody(
  confirmToken: string | undefined,
  options: DestructiveApplyOptions,
): Record<string, unknown> {
  return {
    ...(confirmToken === undefined ? {} : { confirmToken }),
    ...(options.expectedAffectedRows === undefined
      ? {}
      : { expectedAffectedRows: [...options.expectedAffectedRows] }),
  };
}

/**
 * TWO different confirmations, and they must not be confused.
 *
 * The UI's token is what the USER typed (the api name of the thing being dropped) - the
 * dialog's proof that a human meant it. The SERVER's token is a hash of the migration plan,
 * which the browser cannot know before it has been shown the plan. So the user's token is
 * verified here as "a confirmation happened" and never sent as the server's, and the plan
 * token is fetched from the refusal (see `destructive`). Sending the typed name as the plan
 * token is what produced a `confirmation_mismatch` for every drop the UI attempted.
 */
function requireHumanConfirmation(confirmToken: string): void {
  if (confirmToken.trim() === '') {
    throw new ApiError('This change destroys data and was not confirmed.', {
      code: 'confirmation_required',
      status: 409,
    });
  }
}

/**
 * UI `FieldOptions` -> the metadata API's `settings` object.
 *
 * Exported for the round-trip test: this mapper and `toFieldOptions` are the browser's
 * two halves of identity-resolution.md 1.2's ripple, and both silently dropped the
 * identifier flag.
 */
export function settingsOf(input: CreateFieldInput | UpdateFieldInput) {
  const options = input.options;
  if (options === undefined) return {};
  const settings = {
    ...(options.relation === undefined ? {} : { relation: options.relation }),
    ...(options.defaultCurrencyCode === undefined
      ? {}
      : { defaultCurrencyCode: options.defaultCurrencyCode }),
    ...(options.phoneDefaultRegion === undefined
      ? {}
      : { phoneDefaultRegion: options.phoneDefaultRegion }),
    ...(options.isUnique === undefined ? {} : { isUnique: options.isUnique }),
    /**
     * D12. Every PATCH that carries `settings` REPLACES it, so a save that omitted this
     * would clear the record-title flag of the field being edited - silently, and with
     * a success toast.
     */
    ...(options.isLabel === undefined ? {} : { isLabel: options.isLabel }),
    /**
     * Without this the identifier switch was WRITE-ONLY THEATRE: the admin flipped it,
     * the form reported success, and the flag never left the browser. Nothing errored,
     * and the field simply never became an identifier - which is the exact silent-drop
     * failure 1.2 is written about, one file further out than it names.
     */
    ...(options.identity === undefined ? {} : { identity: options.identity }),
    /**
     * file-attachments.md D10, and the key ORDER of this object is what makes obligation 44
     * hold: `metadata/diff.ts` compares `settings` with raw `JSON.stringify`, so a cap
     * written in a different position on the second save would read as a settings CHANGE
     * and produce a migration action for a field nobody edited. Appended, once, here.
     */
    ...(options.maxFiles === undefined ? {} : { maxFiles: options.maxFiles }),
    /** inbound-intake.md D18, appended after `maxFiles` for the key-order reason above. */
    ...(options.multiline === undefined ? {} : { multiline: options.multiline }),
    /** phase-b-coach.md D1 and D2, appended in the store's own order for the same reason. */
    ...(options.checkpoint === undefined ? {} : { checkpoint: options.checkpoint }),
    ...(options.computed === undefined ? {} : { computed: options.computed }),
    /** DESIGN.md G2 (BR-RECORD), appended last in the store's own order for the same reason. */
    ...(options.keyFact === undefined ? {} : { keyFact: options.keyFact }),
  };
  return Object.keys(settings).length === 0 ? {} : { settings };
}

function createdObject(result: WireApplyResult): WireObject | undefined {
  for (const action of result.plan.phaseA) {
    if (action.kind === 'CREATE_OBJECT') return action.object;
  }
  return undefined;
}

function createdField(result: WireApplyResult): WireField | undefined {
  for (const action of result.plan.phaseA) {
    if (action.kind === 'CREATE_FIELD') return action.field;
  }
  return undefined;
}

function missingFromPlan(what: string): ApiError {
  return new ApiError(`The server applied the change but its plan named no new ${what}.`, {
    code: 'internal',
    status: 500,
  });
}
